import React from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
  Select,
  SelectItem
} from '@heroui/react';
import { toast } from 'react-toastify';

import { adjustOrganizationTokens, organizationsListParamsAtom } from '@/store';
import { useAtom } from 'jotai';

interface AdjustTokensModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  organization: { id: number; name: string; token_balance?: number } | null;
  onSuccess?: () => void;
}

type AdjustmentType = 'add' | 'deduct';

export function AdjustTokensModal({
  isOpen,
  onOpenChange,
  organization,
  onSuccess
}: AdjustTokensModalProps) {
  const [amount, setAmount] = React.useState('');
  const [type, setType] = React.useState<AdjustmentType>('add');
  const [description, setDescription] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [, setParams] = useAtom(organizationsListParamsAtom);

  const resetForm = React.useCallback(() => {
    setAmount('');
    setType('add');
    setDescription('');
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleSubmit = React.useCallback(async () => {
    if (!organization) return;

    const parsed = amount.trim() ? parseInt(amount, 10) : NaN;
    if (Number.isNaN(parsed) || parsed < 1) {
      toast.error('Please enter a valid amount (minimum 1)');
      return;
    }

    const finalAmount = type === 'add' ? parsed : -parsed;

    if (type === 'deduct') {
      const currentBalance = organization.token_balance ?? 0;
      if (currentBalance < parsed) {
        toast.error(
          `Insufficient quota. Current: ${currentBalance.toLocaleString()}, requested deduction: ${parsed.toLocaleString()}`
        );
        return;
      }
    }

    try {
      setIsSubmitting(true);
      await adjustOrganizationTokens(organization.id, {
        amount: finalAmount,
        description: description.trim() || undefined
      });

      toast.success(
        type === 'add'
          ? `Successfully granted ${parsed.toLocaleString()} event quota to ${organization.name}`
          : `Successfully deducted ${parsed.toLocaleString()} event quota from ${organization.name}`
      );

      setParams((prev) => ({ ...prev, refresh: Date.now() }));
      onSuccess?.();
      onOpenChange(false);
      resetForm();
    } catch (error) {
      // Error handled by axios interceptor
    } finally {
      setIsSubmitting(false);
    }
  }, [
    organization,
    amount,
    type,
    description,
    onOpenChange,
    onSuccess,
    resetForm,
    setParams
  ]);

  const handleClose = React.useCallback(() => {
    resetForm();
    onOpenChange(false);
  }, [onOpenChange, resetForm]);

  const parsedAmount = amount.trim() ? parseInt(amount, 10) : 0;
  const isValidAmount = !Number.isNaN(parsedAmount) && parsedAmount >= 1;
  const currentBalance = organization?.token_balance ?? 0;
  const wouldExceedDeduction = type === 'deduct' && isValidAmount && currentBalance < parsedAmount;

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="md">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              Manage Event Quota
              {organization && (
                <span className="text-default-500 text-sm font-normal">
                  {organization.name} · Available event quota: {currentBalance.toLocaleString()} events
                </span>
              )}
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <Select
                  label="Type"
                  placeholder="Select adjustment type"
                  selectedKeys={type ? [type] : []}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as AdjustmentType | undefined;
                    if (selected) setType(selected);
                  }}
                  isRequired
                >
                  <SelectItem key="add" textValue="Add / Grant">
                    Add / Grant
                  </SelectItem>
                  <SelectItem key="deduct" textValue="Deduct / Revoke">
                    Deduct / Revoke
                  </SelectItem>
                </Select>

                <Input
                  type="number"
                  min={1}
                  label="Amount (events)"
                  placeholder="Enter event quota amount"
                  value={amount}
                  onValueChange={setAmount}
                  isRequired
                  description={
                    type === 'deduct' && isValidAmount
                      ? `Quota after: ${(currentBalance - parsedAmount).toLocaleString()} events`
                      : type === 'add' && isValidAmount
                        ? `Quota after: ${(currentBalance + parsedAmount).toLocaleString()} events`
                        : undefined
                  }
                  color={wouldExceedDeduction ? 'danger' : undefined}
                  errorMessage={
                    wouldExceedDeduction
                      ? `Insufficient quota (current: ${currentBalance.toLocaleString()} events)`
                      : undefined
                  }
                />

                <Textarea
                  label="Description (optional)"
                  placeholder="e.g., Initial grant, Plan upgrade, Manual adjustment"
                  value={description}
                  onValueChange={setDescription}
                  minRows={2}
                  description="For audit logs"
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose}>
                Cancel
              </Button>
              <Button
                color="primary"
                isLoading={isSubmitting}
                onPress={handleSubmit}
                isDisabled={!isValidAmount || wouldExceedDeduction}
              >
                {type === 'add' ? 'Grant Quota' : 'Deduct Quota'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
